import { NotificationInterface } from "@swivl-mono/types";
import { Button, ButtonVariant } from "@swivl-mono/ui";
import { IoIosCheckmarkCircle, IoIosCloseCircle, IoMdInformationCircleOutline } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

import useHasPendingUpdates from "../../Hooks/useHasPendingUpdates";
import NotificationsModel from "../../Models/Notifications/Notifications.Model";
import { PendingUpdateType } from "../../Models/Pending/Pending.Model";

/**
 * Icon per variant. The toast body stays white in every case, so the icon color
 * is the only signal. A failed action reads as red without the whole toast
 * turning into a banner.
 */
const VARIANT_ICONS = {
  success: <IoIosCheckmarkCircle className="size-6 shrink-0 text-teal-500" />,
  error: <IoIosCloseCircle className="size-6 shrink-0 text-red-600" />,
  info: <IoMdInformationCircleOutline className="size-6 shrink-0 text-yellow-700" />
};

interface BaseToastGenericProps {
  notification: NotificationInterface;
  onAnimateOut: () => Promise<void>;
}

const BaseToastGeneric = (props: BaseToastGenericProps) => {
  const { notification, onAnimateOut } = props;
  const navigate = useNavigate();
  const isPending = useHasPendingUpdates([{ type: PendingUpdateType.NOTIFICATION_ACKNOWLEDGED, id: notification.id }]);

  const handleDismiss = async (): Promise<void> => {
    await onAnimateOut();
    NotificationsModel.acknowledgeNotification(notification);
  };

  const handleAction = async (): Promise<void> => {
    const action = notification.data?.action;
    if (action?.href) {
      window.location.href = action.href;
    } else if (action?.path) {
      navigate(action.path);
    } else {
      return;
    }
    await onAnimateOut();
    NotificationsModel.acknowledgeNotification(notification);
  };

  if (notification.data?.iconDismiss) {
    return (
      <div className="relative flex w-full flex-col gap-2 pr-5 @sm:flex-row @sm:items-center @sm:justify-between @sm:gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {VARIANT_ICONS[notification.data?.variant ?? "info"]}
          <div className="flex min-w-0 flex-col justify-center">
            <span className="text-sm font-semibold text-black">{notification.data?.title || "System Alert"}</span>
            {notification.data?.message && <span className="text-xs break-words text-grey-600 lg:text-sm">{notification.data?.message}</span>}
          </div>
        </div>
        {notification.data?.action && (
          <Button loading={isPending} onClick={handleAction} variant={ButtonVariant.PRIMARY} className="self-center">
            {notification.data.action.label}
          </Button>
        )}
        <button onClick={handleDismiss} aria-label="Dismiss" className="absolute -top-1 -right-1 cursor-pointer text-grey-400 hover:text-grey-600">
          <IoClose className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3">
        {VARIANT_ICONS[notification.data?.variant ?? "info"]}
        <div className="flex flex-col justify-center">
          {/* title */}
          <span className="text-sm font-semibold text-black">{notification.data?.title || "System Alert"}</span>
          {/* subtitle */}
          {notification.data?.message && <span className="text-xs text-grey-600 lg:text-sm">{notification.data?.message}</span>}
        </div>
      </div>

      <div className="flex gap-2">
        {/* action button */}
        {notification.data?.action && <Button onClick={handleAction}>{notification.data.action.label}</Button>}
        {/* dismiss button */}
        <Button loading={isPending} onClick={handleDismiss} variant={ButtonVariant.PRIMARY}>
          Dismiss
        </Button>
      </div>
    </>
  );
};

export const ToastGeneric = BaseToastGeneric;
